import { LOG_LEVEL } from './config';

import lt from 'lodash/lt';
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import property from 'lodash/property';

/**
 * Logger level
 *
 * @class Level
 */
class Level {
   /**
    * Creates an instance of Level.
    *
    * @param {number} value weight of level
    * @param {string} name  name of logger function
    */
   constructor(value, name) {
      this.value = value;
      this.name = name;
   }
}

/* eslint no-magic-numbers:0 */
export const LEVEL = Object.freeze({
   OFF   : new Level(99),
   FATAL : new Level(5,  'error'),
   ERROR : new Level(4,  'error'),
   WARN  : new Level(3,  'warn'),
   LOG   : new Level(2,  'log'),
   DEBUG : new Level(1,  'debug')
});

let defaultLevel = LEVEL[LOG_LEVEL] || LEVEL.WARN;

/**
 * Service for logging simple entity
 *
 * @class LoggerService
 */
export default class Logger {
   /**
    * Creates an instance of LoggerService.
    *
    * @constructor
    * @param {string} name name of logger (will added to log data)
    * @param {Level} [level=defaultLevel] level for log
    */
   constructor(name, level = defaultLevel) {
      this.name = name;
      this.level = level;
   }

   /**
    * add messages with level `Level.DEBUG`
    * @returns {void}
    */
   debug(...args) {
      _push(LEVEL.DEBUG, this.level, this.name, args);
   }

   /**
    * add messages with level `Level.LOG`
    * @returns {void}
    */
   log(...args) {
      _push(LEVEL.LOG, this.level, this.name, args);
   }

   /**
    * add messages with level `Level.WARN`
    * @returns {void}
    */
   warn(...args) {
      _push(LEVEL.WARN, this.level, this.name, args);
   }

   /**
    * add messages with level `Level.ERROR`
    * @returns {void}
    */
   error(...args) {
      _push(LEVEL.ERROR, this.level, this.name, args);
   }

   /**
    * add messages with level `Level.ERROR`
    * @param {Object} report report from `TraceKit`
    * @returns {void}
    */
   fatal(report) {
      _push(LEVEL.ERROR, this.level, this.name, [report.message], report.stack);
   }
}

class LogData {
   constructor(source, level, messages, stack) {
      this.level = level;
      this.messages = messages;
      this.source = source;
      this.stack = map(stack || [], property('url')).join('\n');
   }
   toServer() {
      return {
         level: this.level.value,
         message: this.messages.join(' '),
         source: this.source,
         stack: this.stack
      };
   }
   toConsole() {
      return [this.source, this.level.name.toUpperCase(), ...this.messages, this.stack];
   }
}

/**
 * log message if needed
 *
 * @param {Level} methodLevel log level of method
 * @param {Level} loggerLevel log level of instance of logger
 * @param {string} loggerName name of instance of logger
 * @param {string[]} args data for log
 * @param {string?} stack stack trace
 * @returns {void}
 */
function _push(methodLevel, loggerLevel, loggerName, args, stack) {
   if (lt(methodLevel.value, loggerLevel.value)) {
      return;
   }

   // TODO add time and username/userid if needed
   const data = new LogData(loggerName, methodLevel, args, stack);

   // eslint-disable-next-line no-console
   if ( isFunction(console[methodLevel.name]) ) {
      // eslint-disable-next-line no-console
      console[methodLevel.name].apply(console, data.toConsole());
   }
}
